import React from 'react';
import { X, Trash2, ShoppingBag, ArrowRight, AlertTriangle } from 'lucide-react';
import { getProductImageUrl } from '../../services/api';

const CartDrawer = ({ isOpen, onClose, cartItems = [], onRemoveItem, onUpdateQuantity }) => {
  if (!isOpen) return null;

  // Calculate subtotal for available items
  const validItems = cartItems.filter(item => !item.is_out_of_stock && (item.stock === undefined || item.stock > 0));
  const subtotal = validItems.reduce((acc, item) => {
    const p = Number(item.price || item.sale_price || 0);
    return acc + p * (item.quantity || 1);
  }, 0);

  const hasOutOfStockItems = cartItems.some(item => item.is_out_of_stock || item.stock === 0);

  return (
    <div className="cart-drawer-overlay" onClick={onClose}>
      <div className="cart-drawer-container" onClick={(e) => e.stopPropagation()}>
        
        {/* Drawer Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ShoppingBag size={20} color="#dc2626" />
            <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a' }}>Your Shopping Cart</h3>
          </div>

          <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}>
            <X size={18} />
          </button>
        </div>

        {/* Out of Stock Warning Banner */}
        {hasOutOfStockItems && (
          <div style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px', color: '#dc2626', fontWeight: 800 }}>
            <AlertTriangle size={18} style={{ flexShrink: 0 }} />
            <span>Some items in your cart are out of stock. Please remove them to proceed with checkout.</span>
          </div>
        )}

        {/* Drawer Cart Items Body */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', background: '#ffffff' }}>
          {cartItems.length === 0 ? (
            <div style={{ padding: '60px 0', textAlign: 'center', color: '#64748b' }}>
              <ShoppingBag size={48} color="#cbd5e1" style={{ marginBottom: '12px' }} />
              <p style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>Your cart is empty</p>
              <p style={{ fontSize: '13px', marginTop: '4px' }}>Add a Red Light Therapy Panel to get started!</p>
            </div>
          ) : (
            cartItems.map((item) => {
              const isOut = item.is_out_of_stock || item.stock === 0;
              const stockTag = item.stock_tag || (isOut ? 'Out of Stock' : null);

              return (
                <div
                  key={item.cart_item_id || item.id}
                  style={{
                    display: 'flex',
                    gap: '14px',
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: '16px',
                    opacity: isOut ? 0.75 : 1
                  }}
                >
                  {/* Thumbnail */}
                  <div style={{ width: '76px', height: '76px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', flexShrink: 0, position: 'relative' }}>
                    <img src={getProductImageUrl(item.image_url || item.image)} alt={item.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                  </div>

                  {/* Details */}
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: '14.5px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>{item.name}</h4>
                    
                    {/* Optional Add-ons List */}
                    {Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '4px 0 6px 0' }}>
                        {item.selected_addons.map((addon, aIdx) => (
                          <span key={aIdx} style={{ fontSize: '10px', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fee2e2', padding: '1px 6px', borderRadius: '4px' }}>
                            + {addon.addon_name || addon.name} (+£{Number(addon.addon_price || addon.price || 0).toFixed(2)})
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Out of Stock or Low Stock Badge */}
                    {stockTag && (
                      <div style={{ display: 'inline-block', fontSize: '11px', fontWeight: 800, color: isOut ? '#dc2626' : '#d97706', background: isOut ? '#fef2f2' : '#fffbeb', border: `1px solid ${isOut ? '#fecaca' : '#fef3c7'}`, padding: '2px 8px', borderRadius: '6px', marginBottom: '6px' }}>
                        {stockTag}
                      </div>
                    )}

                    <div style={{ fontSize: '15px', fontWeight: 900, color: isOut ? '#94a3b8' : '#dc2626', marginBottom: '8px' }}>
                      £{Number(item.price || item.sale_price || 0).toFixed(2)}
                    </div>

                    {/* Quantity Controls */}
                    {!isOut && onUpdateQuantity && (
                      <div style={{ display: 'inline-flex', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '2px 6px', gap: '8px' }}>
                        <button
                          onClick={() => onUpdateQuantity(item.id || item.product_id, -1, item.available_stock || item.stock)}
                          style={{ width: '22px', height: '22px', border: 'none', background: '#ffffff', color: '#0f172a', fontWeight: 900, borderRadius: '4px', cursor: 'pointer' }}
                        >
                          -
                        </button>
                        <span style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a' }}>{item.quantity || 1}</span>
                        <button
                          onClick={() => onUpdateQuantity(item.id || item.product_id, 1, item.available_stock || item.stock)}
                          style={{ width: '22px', height: '22px', border: 'none', background: '#ffffff', color: '#0f172a', fontWeight: 900, borderRadius: '4px', cursor: 'pointer' }}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={() => onRemoveItem && onRemoveItem(item.cart_item_id || item.id)}
                    style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', alignSelf: 'flex-start', padding: '4px' }}
                    title="Remove from cart"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Drawer Footer Checkout */}
        {cartItems.length > 0 && (
          <div style={{ padding: '20px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: '#64748b' }}>
              <span>Subtotal</span>
              <span style={{ fontWeight: 900, color: '#0f172a', fontSize: '18px' }}>£{subtotal.toFixed(2)}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', fontSize: '13.5px', color: '#64748b' }}>
              <span>Shipping</span>
              <span style={{ fontWeight: 800, color: '#16a34a' }}>FREE UK Express</span>
            </div>

            <button
              disabled={hasOutOfStockItems}
              className="btn-red-pill"
              style={{
                width: '100%',
                justifyContent: 'center',
                height: '48px',
                borderRadius: '12px',
                fontSize: '15px',
                opacity: hasOutOfStockItems ? 0.6 : 1,
                cursor: hasOutOfStockItems ? 'not-allowed' : 'pointer'
              }}
            >
              <span>{hasOutOfStockItems ? 'Remove Out of Stock Items' : 'Proceed to Checkout'}</span>
              <ArrowRight size={18} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default CartDrawer;
